/**
 * @fileoverview Reusable dialog for picking students to add to a class roster.
 *
 * Ported from the mobile `AddStudentsSheet`. Fetches the instructor's roster,
 * excludes students already in the class (`existingIds`), supports a search
 * filter and multi-select, and calls `onConfirm(ids)` with the chosen
 * student_ids. Web equivalent: Radix Dialog + plain `useState`.
 */

import { useState, useEffect, useMemo } from "react";
import { Search, Check } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { toast } from "sonner";

/**
 * @param {object} props
 * @param {boolean} props.open
 * @param {(open: boolean) => void} props.onOpenChange
 * @param {string[]} [props.existingIds] - Student ids already in the class (excluded from the picker).
 * @param {(ids: string[]) => void} props.onConfirm - Called with the selected student_ids.
 * @returns {React.ReactElement}
 */
const AddStudentsDialog = ({ open, onOpenChange, existingIds = [], onConfirm }) => {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [picked, setPicked] = useState([]);

  useEffect(() => {
    if (!open) return undefined;
    // Reset selection state each time the dialog opens.
    setQuery("");
    setPicked([]);
    const controller = new AbortController();
    setLoading(true);
    api
      .get("/students", { signal: controller.signal })
      .then((res) => setStudents(res.data || []))
      .catch((_err) => {
        if (!controller.signal.aborted) toast.error("Failed to load students");
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [open]);

  const available = useMemo(
    () => students.filter((s) => !existingIds.includes(s.student_id)),
    [students, existingIds]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return available;
    return available.filter((s) =>
      `${s.first_name || ""} ${s.last_name || ""}`.toLowerCase().includes(q)
    );
  }, [available, query]);

  const togglePick = (id) => {
    setPicked((prev) => (prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]));
  };

  const handleConfirm = () => {
    if (picked.length === 0) return;
    onConfirm(picked);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[var(--studio-overlay)] border border-[var(--studio-divider-strong)] text-[var(--text-primary)] mx-4 max-w-[calc(100%-2rem)] md:max-w-md"
        data-testid="add-students-dialog"
      >
        <DialogHeader>
          <DialogTitle className="text-xl font-light" style={{ fontFamily: "Outfit, sans-serif" }}>
            Add students
          </DialogTitle>
        </DialogHeader>

        <div className="relative mt-2">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--text-quaternary)]" />
          <Input
            placeholder="Search students..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="pl-10 bg-[var(--studio-base)] border-[var(--studio-divider-strong)] text-[var(--text-primary)] h-11 text-base placeholder:text-[var(--text-quaternary)]"
            data-testid="add-students-search"
          />
        </div>

        <div className="max-h-[50vh] overflow-y-auto space-y-1 mt-2">
          {loading ? (
            <p className="text-[var(--text-tertiary)] text-sm py-8 text-center">Loading...</p>
          ) : filtered.length === 0 ? (
            <p className="text-[var(--text-tertiary)] text-sm py-8 text-center" data-testid="add-students-empty">
              {available.length === 0 ? "Everyone is already in this class." : "No students match."}
            </p>
          ) : (
            filtered.map((s) => {
              const sel = picked.includes(s.student_id);
              const fullName = `${s.first_name || ""} ${s.last_name || ""}`.trim();
              return (
                <button
                  key={s.student_id}
                  type="button"
                  onClick={() => togglePick(s.student_id)}
                  aria-pressed={sel}
                  className={`w-full flex items-center gap-3 p-3 rounded-xl transition-colors text-left ${
                    sel ? "bg-[var(--accent-yellow-dim)]" : "hover:bg-[var(--studio-interactive)]"
                  }`}
                  data-testid={`add-student-option-${s.student_id}`}
                >
                  <div className="w-9 h-9 bg-[var(--studio-interactive)] rounded-full flex items-center justify-center text-[var(--accent-yellow-text)] font-medium shrink-0">
                    {(s.first_name || "?").charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[var(--text-primary)] text-sm font-medium truncate">{fullName}</p>
                    {s.instruments?.length > 0 && (
                      <p className="text-[var(--text-quaternary)] text-xs truncate">{s.instruments.join(" · ")}</p>
                    )}
                  </div>
                  <div
                    className={`w-6 h-6 rounded-md border flex items-center justify-center shrink-0 ${
                      sel ? "bg-[var(--accent-yellow)] border-[var(--accent-yellow-border)]" : "border-[var(--studio-divider-strong)]"
                    }`}
                  >
                    {sel && <Check className="w-4 h-4 text-[var(--text-inverse)]" />}
                  </div>
                </button>
              );
            })
          )}
        </div>

        <div className="flex gap-3 pt-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="flex-1 border-[var(--studio-divider-strong)] text-[var(--text-primary)] hover:bg-[var(--studio-interactive)] h-11 touch-target"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleConfirm}
            disabled={picked.length === 0}
            className="flex-1 bg-[var(--accent-yellow)] text-[var(--text-inverse)] h-11 touch-target disabled:opacity-50"
            data-testid="add-students-confirm"
          >
            {picked.length > 0 ? `Add ${picked.length}` : "Add"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AddStudentsDialog;
